/**
 * Blocked approvals in Overview's Needs you (R-HL-1, R-HL-3): a session an expired or denied
 * request stopped, one click from the answer sheet (the same sheet a request opens), where Approve
 * and resume is, with Open session beside it. Listed with
 * the pending approvals, before everything else. A resume in progress does not need anyone.
 */
import type { Entities } from '../../lib/store.ts';
import type { NeedItem } from '../overview/needsYou.ts';
import type { BlockedItem } from './blocked.ts';

export function blockedNeeds(blocked: readonly BlockedItem[], e: Pick<Entities, 'machines' | 'projects' | 'sessions'>): NeedItem[] {
  return blocked
    .filter((b) => b.block.phase !== 'resuming')
    .map(({ approval: a, block }) => {
      const where = [a.projectId ? e.projects[a.projectId]?.name : null, e.machines[a.machineId]?.name ?? 'Unknown machine'].filter(Boolean).join(' on ');
      const session = e.sessions[a.sessionId]?.title;
      return {
        key: `b:${a.id}`,
        kind: 'session',
        title: block.label,
        detail: `${a.title}${session ? ` in "${session}"` : ''}.`,
        where,
        at: a.decidedAt ?? a.createdAt,
        to: { approval: a.id },
        // A redirect leads with Mark as handled in its drawer, so the row never says Resume.
        verb: block.resumable && block.lead !== 'handled' ? 'Resume' : 'Review',
      };
    });
}

/** Blocked approvals first; nothing else is dropped (an ended session is not in Needs you on its own). */
export function withBlocked(items: NeedItem[], blocked: readonly BlockedItem[], e: Pick<Entities, 'machines' | 'projects' | 'sessions'>): NeedItem[] {
  const extra = blockedNeeds(blocked, e);
  if (!extra.length) return items;
  // The session's own row would say the same thing: the blocked row stands for it.
  const shown = new Set(extra.map((i) => i.key));
  const covered = new Set(blocked.filter((b) => shown.has(`b:${b.approval.id}`)).map((b) => `s:${b.approval.sessionId}`));
  return [...extra, ...items.filter((i) => !covered.has(i.key))];
}
